import axios, { AxiosInstance } from 'axios';
import { User, Course, ChatMessage, CodeExecutionResult, Progress } from '../types';

// Chapter types
export interface Chapter {
  id: number;
  course_id: number;
  chapter_number: number;
  title: string;
  content: string | null;
  outline: string | null;
  status: 'draft' | 'generated' | 'published' | 'generating' | 'draft_ready' | 'generation_failed';
  order: number;
  created_at: string;
  updated_at: string;
}

export interface ChapterDraft {
  draft_id: number;
  chapter_id: number;
  content: string;
  change_description: string;
  created_at: string;
}

// Use relative URL to work with Nginx reverse proxy
// Nginx will proxy /api/ to backend's /api/v1/
export const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 600000, // 10 minutes timeout for AI generation
    });

    // Add auth token to requests
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  // Auth
  async login(username: string, password: string): Promise<{ access_token: string; user: User }> {
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);
    
    const response = await this.client.post('/auth/login', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return response.data;
  }

  async register(username: string, email: string, password: string, role: string = 'student', full_name?: string): Promise<User> {
    const response = await this.client.post('/auth/register', {
      username,
      email,
      password,
      role,
      full_name,
    });
    return response.data;
  }

  async getMe(): Promise<User> {
    const response = await this.client.get('/auth/me');
    return response.data;
  }

  // Student - Courses
  async getCourses(): Promise<Course[]> {
    const response = await this.client.get('/student/courses');
    return response.data;
  }

  async getCourse(courseId: number): Promise<Course> {
    const response = await this.client.get(`/student/courses/${courseId}`);
    return response.data;
  }

  async getPublishedChapters(courseId: number): Promise<Chapter[]> {
    const response = await this.client.get(`/student/courses/${courseId}/chapters`);
    return response.data;
  }

  // Student - Chat
  async chat(messages: ChatMessage[], context?: string): Promise<ReadableStream<Uint8Array> | { message: ChatMessage }> {
    const response = await this.client.post('/student/chat', {
      messages,
      context,
      stream: true,
    }, {
      responseType: 'stream',
    });
    return response.data;
  }

  async askAboutSelection(selectedText: string, question: string, sectionId?: string): Promise<ReadableStream<Uint8Array>> {
    const response = await this.client.post('/student/ask-selection', {
      selected_text: selectedText,
      question,
      section_id: sectionId,
    }, {
      responseType: 'stream',
    });
    return response.data;
  }

  // Student - Code Execution
  async executeCode(code: string, timeout?: number): Promise<CodeExecutionResult> {
    const response = await this.client.post('/student/execute-code', {
      code,
      timeout,
    });
    return response.data;
  }

  async checkSolution(code: string, testCases: any[]): Promise<any> {
    const response = await this.client.post('/student/check-solution', {
      code,
      test_cases: testCases,
    });
    return response.data;
  }

  // Alias for backward compatibility
  async check_code_solution(code: string, testCases: any[]): Promise<any> {
    return this.checkSolution(code, testCases);
  }

  // Student - Progress
  async getProgress(courseId: number): Promise<Progress[]> {
    const response = await this.client.get(`/student/progress/${courseId}`);
    return response.data;
  }

  async updateProgress(courseId: number, sectionId: string, completed: boolean): Promise<Progress> {
    const response = await this.client.post('/student/progress', {
      course_id: courseId,
      section_id: sectionId,
      completed,
    });
    return response.data;
  }

  // Teacher - Courses
  async getTeacherCourses(): Promise<Course[]> {
    const response = await this.client.get('/teacher/courses');
    return response.data;
  }

  async createCourse(title: string, description: string, outline: string): Promise<Course> {
    const response = await this.client.post('/teacher/courses', {
      title,
      description,
      outline,
    });
    return response.data;
  }

  async getTeacherCourse(courseId: number): Promise<Course> {
    const response = await this.client.get(`/teacher/courses/${courseId}`);
    return response.data;
  }

  async updateCourse(courseId: number, updates: Partial<Course>): Promise<Course> {
    const response = await this.client.put(`/teacher/courses/${courseId}`, updates);
    return response.data;
  }

  async deleteCourse(courseId: number): Promise<void> {
    await this.client.delete(`/teacher/courses/${courseId}`);
  }

  async regenerateContent(courseId: number, outline: string, sectionId?: string, instructions?: string): Promise<{ content: string; html_content: string }> {
    const response = await this.client.post(`/teacher/courses/${courseId}/generate`, {
      outline,
      section_id: sectionId,
      instructions,
    });
    return response.data;
  }

  async editContent(courseId: number, currentContent: string, instructions: string): Promise<{ content: string; html_content: string }> {
    const response = await this.client.post(`/teacher/courses/${courseId}/edit`, {
      current_content: currentContent,
      instructions,
    });
    return response.data;
  }

  async publishCourse(courseId: number): Promise<Course> {
    const response = await this.client.post(`/teacher/courses/${courseId}/publish`);
    return response.data;
  }

  async unpublishCourse(courseId: number): Promise<Course> {
    const response = await this.client.post(`/teacher/courses/${courseId}/unpublish`);
    return response.data;
  }

  async expandOutline(outline: string): Promise<ReadableStream<Uint8Array>> {
    const response = await this.client.post('/teacher/expand-outline', { outline }, {
      responseType: 'stream',
    });
    return response.data;
  }

  // Student - Chat History
  async getChatHistory(courseId: number): Promise<ChatMessage[]> {
    const response = await this.client.get(`/student/chat-history/${courseId}`);
    return response.data;
  }

  async saveChatHistory(courseId: number, messages: ChatMessage[]): Promise<void> {
    await this.client.post(`/student/chat-history/${courseId}`, messages);
  }

  async clearChatHistory(courseId: number): Promise<void> {
    await this.client.delete(`/student/chat-history/${courseId}`);
  }

  // Teacher - Chapters
  async getChapters(courseId: number): Promise<Chapter[]> {
    const response = await this.client.get(`/teacher/chapters/course/${courseId}`);
    return response.data;
  }

  async createChapter(courseId: number, data: { chapter_number: number; title: string; order: number; outline?: string; content?: string }): Promise<Chapter> {
    const response = await this.client.post(`/teacher/chapters/course/${courseId}`, data);
    return response.data;
  }

  async getChapter(chapterId: number): Promise<Chapter> {
    const response = await this.client.get(`/teacher/chapters/${chapterId}`);
    return response.data;
  }

  async updateChapter(chapterId: number, updates: Partial<Chapter>): Promise<Chapter> {
    const response = await this.client.put(`/teacher/chapters/${chapterId}`, updates);
    return response.data;
  }

  async deleteChapter(chapterId: number): Promise<void> {
    await this.client.delete(`/teacher/chapters/${chapterId}`);
  }

  async publishChapter(chapterId: number): Promise<{ message: string; chapter_id: number; chapters_synced: number }> {
    const response = await this.client.post(`/teacher/chapters/${chapterId}/publish`);
    return response.data;
  }

  async unpublishChapter(chapterId: number): Promise<{ message: string; chapter_id: number; chapters_synced: number }> {
    const response = await this.client.post(`/teacher/chapters/${chapterId}/unpublish`);
    return response.data;
  }

  async syncChapterContent(chapterId: number): Promise<{ message: string; chapter_id: number; chapters_synced: number; total_length: number }> {
    const response = await this.client.post(`/teacher/chapters/${chapterId}/sync`);
    return response.data;
  }

  async saveChapterContent(chapterId: number, content: string): Promise<void> {
    await this.client.post(`/teacher/chapters/${chapterId}/content`, { content });
  }

  async editChapterContent(
    chapterId: number,
    currentContent: string,
    instructions: string,
    targetSectionId?: string,
    selectedHtml?: string
  ): Promise<ChapterDraft> {
    const response = await this.client.post(`/teacher/chapters/${chapterId}/edit`, {
      current_content: currentContent,
      instructions,
      target_section_id: targetSectionId,
      selected_html: selectedHtml,
    });
    return {
      draft_id: response.data.draft_id,
      chapter_id: response.data.chapter_id,
      content: response.data.content,
      change_description: response.data.change_description || instructions,
      created_at: new Date().toISOString(),
    };
  }

  // Teacher - Chapter Drafts
  async generateChapterContent(chapterId: number, outline: string, instructions?: string): Promise<ChapterDraft> {
    const response = await this.client.post(`/teacher/chapters/${chapterId}/generate`, {
      outline,
      instructions,
    });
    return {
      draft_id: response.data.draft_id,
      chapter_id: response.data.chapter_id,
      content: response.data.content,
      change_description: response.data.change_description || '',
      created_at: new Date().toISOString(),
    };
  }

  async getDrafts(chapterId: number): Promise<ChapterDraft[]> {
    const response = await this.client.get(`/teacher/chapters/${chapterId}/drafts`);
    return response.data.map((draft: any) => ({
      draft_id: draft.draft_id ?? draft.id,
      chapter_id: draft.chapter_id,
      content: draft.content ?? draft.preview ?? '',
      change_description: draft.change_description || '',
      created_at: draft.created_at,
    }));
  }

  async getDraft(draftId: number): Promise<ChapterDraft & { current_content: string }> {
    const response = await this.client.get(`/teacher/chapters/drafts/${draftId}`);
    return {
      ...response.data,
      draft_id: response.data.draft_id ?? response.data.id,
    };
  }

  async saveDraft(draftId: number): Promise<void> {
    await this.client.post(`/teacher/chapters/drafts/${draftId}/save`);
  }

  async discardDraft(draftId: number): Promise<void> {
    await this.client.delete(`/teacher/chapters/drafts/${draftId}`);
  }

  async syncCourseContent(courseId: number): Promise<{ message: string; chapters_synced: number }> {
    const response = await this.client.post(`/teacher/chapters/course/${courseId}/sync`);
    return response.data;
  }
}

export const api = new ApiService();
